'use client'

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

interface ProgressiveComponentLoaderProps<T> {
	components: T[]
	renderItem: (component: T, index: number) => ReactNode
	initialCount?: number
	rootMargin?: string
	fallbackMs?: number
}

export default function ProgressiveComponentLoader<T>({
	components,
	renderItem,
	initialCount = 2,
	rootMargin = '100px',
	fallbackMs = 2000,
}: ProgressiveComponentLoaderProps<T>) {
	const sentinelRef = useRef<HTMLDivElement | null>(null)
	const [loadedCount, setLoadedCount] = useState(() =>
		Math.min(initialCount, components.length)
	)

	useEffect(() => {
		setLoadedCount(Math.min(initialCount, components.length))
	}, [components.length, initialCount])

	useEffect(() => {
		if (loadedCount >= components.length) return

		const prefersReducedMotion =
			typeof window !== 'undefined' &&
			window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches

		const effectiveType =
			typeof navigator !== 'undefined' &&
			'connection' in navigator &&
			typeof (navigator as Navigator & { connection?: { effectiveType?: string } })
				.connection?.effectiveType === 'string'
				? (navigator as Navigator & { connection?: { effectiveType?: string } })
						.connection?.effectiveType
				: undefined

		if (prefersReducedMotion || effectiveType === '4g') {
			setLoadedCount(components.length)
			return
		}

		const node = sentinelRef.current
		if (!node) return

		const observer = new IntersectionObserver(
			entries => {
				if (entries.some(entry => entry.isIntersecting)) {
					setLoadedCount(components.length)
				}
			},
			{ rootMargin }
		)

		observer.observe(node)

		const timeoutId = window.setTimeout(() => {
			setLoadedCount(components.length)
		}, fallbackMs)

		return () => {
			observer.disconnect()
			window.clearTimeout(timeoutId)
		}
	}, [components.length, fallbackMs, loadedCount, rootMargin])

	if (components.length === 0) return null

	return (
		<>
			{components.slice(0, loadedCount).map(renderItem)}
			{loadedCount < components.length && (
				<>
					<div ref={sentinelRef} className='h-0' aria-hidden='true' />
					<span className='sr-only'>Loading content</span>
				</>
			)}
		</>
	)
}
